import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import trainingbg from "../assets/training.jpg";
import LineBreak from "./LineBreak";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import AssistantIcon from "@mui/icons-material/Assistant";
import SwitchAccessShortcutAddIcon from "@mui/icons-material/SwitchAccessShortcutAdd";
import CircleIcon from "@mui/icons-material/Circle";
import GroupIcon from "@mui/icons-material/Group";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import DangerousIcon from "@mui/icons-material/Dangerous";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ConstructionIcon from "@mui/icons-material/Construction";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import EmailIcon from "@mui/icons-material/Email";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";

export default function TrainingRatesSection(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const basicItems =
    (t("trainingRates.basicItems", {
      returnObjects: true,
    }) as string[]) || [];

  const uspsaItems =
    (t("trainingRates.uspsaItems", {
      returnObjects: true,
    }) as string[]) || [];

  const advancedItems =
    (t("trainingRates.advancedItems", {
      returnObjects: true,
    }) as string[]) || [];

  const privateTableHeaders =
    (t("trainingRates.privateTableHeaders", {
      returnObjects: true,
    }) as string[]) || [];
  const privateTableRows =
    (t("trainingRates.privateTableRows", {
      returnObjects: true,
    }) as {
      type: string;
      startRate: string;
      extendedRate: string;
    }[]) || [];

  const smallTableHeaders =
    (t("trainingRates.smallTableHeaders", {
      returnObjects: true,
    }) as string[]) || [];
  const smallTableRows =
    (t("trainingRates.smallTableRows", {
      returnObjects: true,
    }) as {
      type: string;
      half: string;
      full: string;
    }[]) || [];

  const smallApproxTableHeaders =
    (t("trainingRates.smallApproxTableHeaders", {
      returnObjects: true,
    }) as string[]) || [];
  const smallApproxTableRows =
    (t("trainingRates.smallApproxTableRows", {
      returnObjects: true,
    }) as {
      rate: string;
      three: string;
      four: string;
      five: string;
    }[]) || [];

  const regularTableHeaders =
    (t("trainingRates.regularTableHeaders", {
      returnObjects: true,
    }) as string[]) || [];
  const regularTableRows =
    (t("trainingRates.regularTableRows", {
      returnObjects: true,
    }) as {
      type: string;
      full: string;
    }[]) || [];

  const regularApproxTableHeaders =
    (t("trainingRates.regularApproxTableHeaders", {
      returnObjects: true,
    }) as string[]) || [];
  const regularApproxTableRows =
    (t("trainingRates.regularApproxTableRows", {
      returnObjects: true,
    }) as {
      rate: string;
      six: string;
      eight: string;
      ten: string;
      twelve: string;
    }[]) || [];

  const notIncludeItems =
    (t("trainingRates.notIncludeItems", {
      returnObjects: true,
    }) as string[]) || [];

  const equipItems =
    (t("trainingRates.equipItems", {
      returnObjects: true,
    }) as string[]) || [];

  const noteItems =
    (t("trainingRates.noteItems", {
      returnObjects: true,
    }) as string[]) || [];

  const bookItems =
    (t("trainingRates.bookItems", {
      returnObjects: true,
    }) as string[]) || [];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "visible", // allow text to exceed image height
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${trainingbg})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 75%",
          opacity: 0.25,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="trainingRates"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-400px" }, // pulls text upward over image
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" color="text.primary" gutterBottom>
              {t("trainingRates.title")}
            </Typography>
          </Box>
          <Box sx={{ mt: 1, mx: "auto", width: { xs: "95%", md: "75%" } }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.start")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 1 }}
            >
              <AdminPanelSettingsIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.privateTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.privateStart")}
            </Typography>
            <TableContainer sx={{ mt: 1, border: 1, borderColor: "divider" }}>
              <Table
                size="small"
                aria-label={t("trainingRates.tableLabel")}
                sx={{
                  borderCollapse: "collapse",
                  "& .MuiTableCell-root": {
                    border: 1,
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {privateTableHeaders.map((header) => (
                      <TableCell key={header} sx={{ fontWeight: "bold" }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {privateTableRows.map((row) => (
                    <TableRow key={row.type}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {row.type}
                      </TableCell>
                      <TableCell>{row.startRate}</TableCell>
                      <TableCell>{row.extendedRate}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 2, lineHeight: 1.2 }}
            >
              {t("trainingRates.privateEnd")}
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SentimentVerySatisfiedIcon
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("trainingRates.basicTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <b>
                <i>{t("trainingRates.basicDesc")}</i>
              </b>
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="trainingRates.basicStart"
                components={{ br: <LineBreak /> }}
              />
            </Typography>
            <List dense>
              {basicItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.basicEnd")}
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <AssistantIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.uspsaTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <b>
                <i>{t("trainingRates.uspsaDesc")}</i>
              </b>
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="trainingRates.uspsaStart"
                components={{ br: <LineBreak /> }}
              />
            </Typography>
            <List dense>
              {uspsaItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.uspsaEnd")}
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SwitchAccessShortcutAddIcon
                sx={{ verticalAlign: "middle", mr: 1 }}
              />
              {t("trainingRates.advancedTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <b>
                <i>{t("trainingRates.advancedDesc")}</i>
              </b>
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="trainingRates.advancedStart"
                components={{ br: <LineBreak /> }}
              />
            </Typography>
            <List dense>
              {advancedItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.advancedEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 1 }}
            >
              <GroupIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.smallTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <b>
                <i>{t("trainingRates.smallDesc")}</i>
              </b>
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.smallStart")}
            </Typography>
            <TableContainer sx={{ mt: 1, border: 1, borderColor: "divider" }}>
              <Table
                size="small"
                aria-label={t("trainingRates.tableLabel")}
                sx={{
                  borderCollapse: "collapse",
                  "& .MuiTableCell-root": {
                    border: 1,
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {smallTableHeaders.map((header) => (
                      <TableCell key={header} sx={{ fontWeight: "bold" }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {smallTableRows.map((row) => (
                    <TableRow key={row.type}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {row.type}
                      </TableCell>
                      <TableCell>{row.half}</TableCell>
                      <TableCell>{row.full}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <b>
                <i>{t("trainingRates.smallApproxTitle")}</i>
              </b>
            </Typography>
            <TableContainer sx={{ mt: 1, border: 1, borderColor: "divider" }}>
              <Table
                size="small"
                aria-label={t("trainingRates.tableLabel")}
                sx={{
                  borderCollapse: "collapse",
                  "& .MuiTableCell-root": {
                    border: 1,
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {smallApproxTableHeaders.map((header) => (
                      <TableCell key={header} sx={{ fontWeight: "bold" }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {smallApproxTableRows.map((row) => (
                    <TableRow key={row.rate}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {row.rate}
                      </TableCell>
                      <TableCell>{row.three}</TableCell>
                      <TableCell>{row.four}</TableCell>
                      <TableCell>{row.five}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 2, lineHeight: 1.2 }}
            >
              {t("trainingRates.smallEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 1 }}
            >
              <Diversity3Icon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.regularTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <b>
                <i>{t("trainingRates.regularDesc")}</i>
              </b>
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.regularStart")}
            </Typography>
            <TableContainer sx={{ mt: 1, border: 1, borderColor: "divider" }}>
              <Table
                size="small"
                aria-label={t("trainingRates.tableLabel")}
                sx={{
                  borderCollapse: "collapse",
                  "& .MuiTableCell-root": {
                    border: 1,
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {regularTableHeaders.map((header) => (
                      <TableCell key={header} sx={{ fontWeight: "bold" }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regularTableRows.map((row) => (
                    <TableRow key={row.type}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {row.type}
                      </TableCell>
                      <TableCell>{row.full}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <i>{t("trainingRates.regularNote")}</i>
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <b>
                <i>{t("trainingRates.regularApproxTitle")}</i>
              </b>
            </Typography>
            <TableContainer sx={{ mt: 1, border: 1, borderColor: "divider" }}>
              <Table
                size="small"
                aria-label={t("trainingRates.tableLabel")}
                sx={{
                  borderCollapse: "collapse",
                  "& .MuiTableCell-root": {
                    border: 1,
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {regularApproxTableHeaders.map((header) => (
                      <TableCell key={header} sx={{ fontWeight: "bold" }}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regularApproxTableRows.map((row) => (
                    <TableRow key={row.rate}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {row.rate}
                      </TableCell>
                      <TableCell>{row.six}</TableCell>
                      <TableCell>{row.eight}</TableCell>
                      <TableCell>{row.ten}</TableCell>
                      <TableCell>{row.twelve}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 2, lineHeight: 1.2 }}
            >
              {t("trainingRates.regularEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 1 }}
            >
              <TipsAndUpdatesIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.infoTitle")}
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <DangerousIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.notIncludeTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.notIncludeStart")}
            </Typography>
            <List dense>
              {notIncludeItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon
                    sx={{
                      "& .MuiSvgIcon-root": {
                        color: "error.main",
                      },
                    }}
                  >
                    <HighlightOffIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.notIncludeEnd")}
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <ConstructionIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.equipTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.equipStart")}
            </Typography>
            <List dense>
              {equipItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.equipEnd")}
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SpeakerNotesIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.noteTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.noteStart")}
            </Typography>
            <List dense>
              {noteItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.noteEnd")}
            </Typography>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <SupportAgentIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.bookTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.bookStart")}
            </Typography>
            <List dense>
              {bookItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.bookEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <QuestionAnswerIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingRates.contactTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingRates.contactContent")}
            </Typography>
            <Box
              sx={{
                mt: 3,
                mx: "auto",
                width: { xs: "95%", md: "75%" },
                textAlign: "center",
              }}
            >
              <Button
                component="a"
                href="mailto:sharkshooter2011@gmail.com"
                endIcon={<EmailIcon />}
                size="large"
                variant="contained"
                sx={{
                  px: 5,
                  py: 1.8,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 999,
                  textTransform: "none",
                  boxShadow: 3,
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 6,
                  },
                }}
              >
                {t("trainingRates.contactBtn")}
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
